import { useEffect, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ContactForm from './ContactForm';
import './ContactModal.css';

const ContactModal = ({ isOpen, onClose }) => {
  const titleId = useId();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);

    const focusTimer = window.setTimeout(() => {
      panelRef.current?.querySelector('input, textarea, button')?.focus();
    }, 180);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="contact-modal"
          className="contact-modal-root"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          onMouseDown={handleBackdropClick}
          data-testid="contact-modal"
        >
          <motion.div
            ref={panelRef}
            className="contact-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ opacity: 0, y: 28, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="contact-modal-header">
              <div className="contact-modal-heading">
                <span className="contact-modal-label">— Contact</span>
                <h2 id={titleId} className="contact-modal-title">
                  Contact
                </h2>
              </div>
              <button
                type="button"
                className="contact-modal-close"
                onClick={onClose}
                aria-label="Close contact form"
                data-testid="contact-modal-close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            <ContactForm isOpen={isOpen} onSuccess={onClose} />

            <p className="contact-modal-legal">
              By sending a message you agree to our{' '}
              <Link to="/privacy" className="contact-modal-legal-link" onClick={onClose}>
                privacy policy
              </Link>
              .
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
